import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  Suspense,
  lazy,
} from "react";
import { connect, ConnectedProps } from "react-redux";
import { DRIVE_TAB } from "@/utils/constants/tabConstants";
import { formatEntityName } from "@/utils/utilFunctions/entityName";
import { Button, Modal, Tooltip, PublishedIcon } from "..";
import {
  trimMedia,
  getSearchedListMedia,
  getPresetList,
  getListMedia,
  getMedia,
} from "@/store/media/media.action";
import {
  emptyAssetDetails,
  emptyMediaDetails,
  setMediaDetails,
} from "@/store/media/media.slice";
import { toast } from "..";
import { updateModal } from "@/store/app/app.slice";
import { RootState } from "../../store";
import { Scissors, Download, Layers2 } from "lucide-react";
import {
  fileProcessingStatusToTextAndColor,
  formatTimestampToAgo,
  mediaStatusTooltipText,
  mediaStatusToTextAndColor,
  secondsToHms,
} from "../../utils";
import { agencyCategoryOptions } from "@/utils/constants/utils";
import { ProgressiveImage } from "../ProgressiveImage/ProgressiveImage";
import { Badge } from "react-bootstrap";
import { callRegisterCustomMediaOnCms } from "@/store/upload/upload.action";
import {
  updateIsFilter,
  updateIsClearFilter,
} from "@/store/media/media.action";
import { useScrollToTop } from "../../hooks/useScrollToTop";
import { getThumbnailUrlUtil } from "@/utils/mediaUtils";
import { handleDownloadMedia } from "@/utils/mediaUtils";
import { getTokenForDownload } from "@/store/media/media.action";
import { MediaModel, ProductModel, PresetListModel } from "@/database";

interface DiscoverCardProps {
  item: MediaModel;
  onPreview: (item: MediaModel) => void;
  useProgressiveLoading?: boolean;
  presetList?: PresetListModel[];
  updateIsFilter: (params: any) => void;
  productList: ProductModel[];
  showUsedBy?: boolean;
  viewMode?: "grid" | "list";
}

type ModalType = "clone" | "mediaDetails" | "socialPublish" | null;

type Props = DiscoverCardProps & ConnectedProps<typeof connector>;

// Lazy load modal components for better performance
const MediaDetails = lazy(
  () => import("../../routes/pages/MediaDetail/MediaDetails")
);
const MediaPublish = lazy(
  () => import("../../routes/pages/MediaPublish/MediaPublish")
);
const VideoTrimmer = lazy(() => import("../VideoTrimmer/VideoTrimmer"));

// Shared component for social icons rendering
interface SocialIconsProps {
  socials: any[] | undefined;
  variant?: "grid" | "list";
}

const SocialIcons: React.FC<SocialIconsProps> = ({
  socials,
  variant = "grid",
}) => {
  if (!socials || !Array.isArray(socials) || socials.length === 0) {
    return null;
  }

  const uniqueJobTypes = [
    ...new Set(socials.map((social: any) => social.job_type)),
  ];

  const chipClassName =
    variant === "grid"
      ? "social-chip p-1 rounded-pill d-flex"
      : "social-chip border p-1 rounded-pill";

  const iconClassName = variant === "grid" ? "w-100 h-100" : "";

  return (
    <>
      {uniqueJobTypes.map((jobType, index) => {
        const socialItem = socials.find(
          (social: any) => social.job_type === jobType
        );
        return (
          <div key={index} className={chipClassName} style={{ height: "22px" }}>
            <PublishedIcon
              job_type={jobType as string}
              status={socialItem?.status || 0}
              className={iconClassName}
            />
          </div>
        );
      })}
    </>
  );
};

const DiscoverCard: React.FC<Props> = ({
  item,
  updateModal,
  trimMedia,
  getSearchedListMedia,
  preMediaList,
  postMediaList,
  onPreview,
  useProgressiveLoading = true,
  presetList,
  getListMedia,
  getPresetList,
  getMedia,
  setMediaDetails,
  currentMediaDetailsForEdit,
  callRegisterCustomMediaOnCms,
  updateIsClearFilter,
  updateIsFilter,
  productList,
  showUsedBy = true,
  getTokenForDownload,
  viewMode = "grid",
}) => {
  const { navigateWithScroll } = useScrollToTop();
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [isTrimmerOpen, setIsTrimmerOpen] = useState(false);
  const [duration, setDuration] = useState(0);
  const [trimmedMedia, setTrimmedMedia] = useState<any>(null);
  const [isPortrait, setIsPortrait] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  useEffect(() => {
    if (item?.meta?.duration && item.meta.duration > 0) {
      const videoDuration = item.meta.duration;
      setDuration(videoDuration);
    } else {
      setDuration(0);
    }
  }, [item?.meta?.duration]);
  useEffect(() => {
    const handleTrimEvent = (event: CustomEvent) => {
      if (event.detail.videoId === item.id) {
        handleTrim();
      }
    };

    const handleCloneEvent = (event: CustomEvent) => {
      if (event.detail.videoId === item.id) {
        handleClone();
      }
    };

    const handleDownloadEvent = (event: CustomEvent) => {
      if (event.detail.videoId === item.id) {
        handleDownload();
      }
    };
    window.addEventListener("triggerTrim", handleTrimEvent as EventListener);
    window.addEventListener("triggerClone", handleCloneEvent as EventListener);
    window.addEventListener(
      "triggerDownload",
      handleDownloadEvent as EventListener
    );
    return () => {
      window.removeEventListener(
        "triggerTrim",
        handleTrimEvent as EventListener
      );
      window.removeEventListener(
        "triggerClone",
        handleCloneEvent as EventListener
      );
      window.removeEventListener(
        "triggerDownload",
        handleDownloadEvent as EventListener
      );
    };
  }, [item.id]);

  useEffect(() => {
    if (item?.images?.base && (item?.images?.thumb || item?.images?.portrait)) {
      const img = new Image();
      const imgSrc = getThumbnailUrlUtil({
        media: item,
        id: item?.images?.thumb || item.images?.portrait,
        source: item?.source,
      });
      img.src = imgSrc;

      const checkPortrait = () => {
        if (img.naturalWidth > 0 && img.naturalHeight > 0) {
          setIsPortrait(img.naturalHeight > img.naturalWidth);
        }
      };

      if (img.complete) {
        checkPortrait();
      } else {
        img.onload = checkPortrait;
        img.onerror = () => {
          setIsPortrait(false);
        };
      }
    } else {
      setIsPortrait(false);
    }
  }, [
    item?.images?.base,
    item?.images?.thumb,
    item?.images?.portrait,
    item?.source,
  ]);

  const handleDownload = useCallback(() => {
    handleDownloadMedia(item, getTokenForDownload);
  }, [item, handleDownloadMedia, getTokenForDownload]);

  const getProductNameById = useCallback(
    (productId: string) => {
      if (!productId) return "N/A";
      const productItem = productList?.find((p: any) => p.id === productId);
      return productItem?.name;
    },
    [productList]
  );

  const getUniqueTeams = useCallback(
    (childMedia: any[] | undefined) => {
      if (!childMedia || childMedia.length === 0) return [];
      const uniqueTeams = childMedia
        .filter(
          (item: any, index: number, self: any[]) =>
            index === self.findIndex((t: any) => t.team === item.team)
        )
        .map((item: any) => getProductNameById(item.team));
      return uniqueTeams;
    },
    [getProductNameById]
  );

  const uniqueTeamsDisplay = useMemo(() => {
    return getUniqueTeams(item.child_media);
  }, [getUniqueTeams, item?.child_media]);

  const handleTrim = () => {
    const effectiveDuration =
      duration > 0 ? duration : item?.meta?.duration || 0;
    if (effectiveDuration > 0) {
      const initialDuration = effectiveDuration;
      setDuration(initialDuration);
      setIsTrimmerOpen(true);
    }
  };

  const handleCardClick = () => {
    onPreview(item);
  };

  const openModal = (modalType: ModalType) => {
    setActiveModal(modalType);
  };

  const closeModal = () => {
    if (trimmedMedia?.asset_type && trimmedMedia.status !== -99) {
      updateIsClearFilter(true);
      updateIsFilter(false);
      const targetPath = `/media/${trimmedMedia.asset_type}`;
      navigateWithScroll(targetPath);
      setTrimmedMedia(null);
    }
    setActiveModal(null);
  };

  const handleEvent = (eventType: "nextClick" | "doneClick" | "backClick") => {
    window.dispatchEvent(
      new CustomEvent(eventType, {
        detail: { id: currentMediaDetailsForEdit?.id },
      })
    );
    if (eventType === "doneClick") {
      if (trimmedMedia?.asset_type) {
        const targetPath = `/media/${trimmedMedia.asset_type}`;
        navigateWithScroll(targetPath);
        setTrimmedMedia(null);
      }
      closeModal();
    } else if (eventType !== "nextClick") {
      closeModal();
    }
  };

  const getAgencyCategoryLabel = (categoryValue: number) => {
    const category = agencyCategoryOptions.find(
      (option) => option.value === categoryValue
    );
    return category?.label || "";
  };

  const getWizardModalTitle = () => {
    if (currentMediaDetailsForEdit?.media_ref?.rel) {
      switch (currentMediaDetailsForEdit.media_ref.rel.toLowerCase()) {
        case "clone":
          return "Create Clone";
        case "simulive":
          return "Create Simulive";
        case "podcast":
          return "Create Podcast";
        case "embed":
          return "Embed Content";
        case "trimmed":
          return "Edit Content";
        default:
          return "Upload Content";
      }
    }
    return "Upload Content";
  };

  const handleClone = useCallback(() => {
    if (item === null) return;

    const data = {
      team: "",
      lang: "",
      id: item.id,
    };

    const mediaType = "clone";
    const mediaName = `media${Date.now()}`;
    const fid = `${item.id}-${Date.now().toString()}`;

    callRegisterCustomMediaOnCms(
      {
        type: mediaType,
        data,
        name: mediaName,
        id: item.id,
        fid,
        // asset_type: "itemhai",
      },
      (resp: any) => {
        updateIsFilter(false);
        if (resp.status === 200) {
          setMediaDetails({
            data: {
              media: {
                ...resp.data,
              },
            },
          });
          setActiveModal("mediaDetails");
        } else {
          toast.fail("");
        }
      }
    );
  }, [item, callRegisterCustomMediaOnCms, updateIsFilter, setMediaDetails]);

  const AgencyCategory =
    item?.agencies && item?.agencies.length > 0
      ? agencyCategoryOptions.find(
          (option) => option.value === item?.agencies?.[0]?.category
        )
      : agencyCategoryOptions[1];

  // Memoize thumbnail URL to avoid recalculating multiple times
  const thumbnailUrl = useMemo(
    () =>
      getThumbnailUrlUtil({
        media: item,
        id: item?.images?.thumb || item?.images?.portrait,
        source: item?.source,
      }),
    [item, item?.images?.thumb, item?.images?.portrait, item?.source]
  );

  return (
    <>
      {viewMode === "grid" && (
        <div className="card shadow-sm">
          <div className="position-relative card-img-top">
            <div className="rounded ratio ratio-16x9 border bg-secondary pointer w-100">
              <ProgressiveImage
                src={thumbnailUrl}
                className="position-absolute top-50 start-50 translate-middle"
                alt="Media Thumbnail"
                style={{
                  height: "100%",
                  width: "100%",
                  objectFit: isPortrait ? "contain" : "cover",
                  opacity: 1,
                }}
                useProgressiveLoading={useProgressiveLoading}
              />
              <div
                className="media-hover-overlay play-button"
                onClick={handleCardClick}
              >
                <i
                  className="bi bi-play-fill text-white"
                  style={{ fontSize: "2rem" }}
                ></i>
              </div>
              <span className="timeCard position-absolute bottom-0 end-0">
                {secondsToHms(item?.meta?.duration)}
              </span>
              <Tooltip
                content={mediaStatusTooltipText(item?.media_status || 0)}
              >
                <span
                  className={`timeCard position-absolute badge bg-success bottom-0`}
                >
                  {
                    mediaStatusToTextAndColor(item?.media_status || 0)
                      .statusText
                  }
                </span>
              </Tooltip>
            </div>
          </div>
          <div className="card-body p-2" style={{ minHeight: "90px" }}>
            <h6
              className="text-truncate fw-medium anchor"
              onClick={handleCardClick}
            >
              {item.title}
            </h6>
            <p className="my-2 text-secondary">
              {item?.agencydata && `${formatEntityName(item?.agencydata)} | `}{" "}
              {formatTimestampToAgo(item?.ct)}
            </p>
            <div className="d-flex align-items-center gap-2">
              <SocialIcons socials={item?.socials} variant="grid" />
              {showUsedBy &&
                uniqueTeamsDisplay.length > 0 &&
                uniqueTeamsDisplay.map((teamName, index) => (
                  <div
                    key={`team-${index}`}
                    className="social-chip p-1 rounded-pill d-flex"
                    style={{ height: "22px" }}
                  >
                    <i
                      className="bi bi-people-fill text-primary me-1"
                      style={{ fontSize: "0.8rem" }}
                    ></i>
                    <span
                      className="text-truncate"
                      style={{ maxWidth: "120px" }}
                    >
                      {teamName}
                    </span>
                  </div>
                ))}
            </div>
          </div>
          <div className="border-top d-flex">
            <div className="d-flex">
              <Badge bg={AgencyCategory?.badge} className="f-10 m-2">
                {AgencyCategory?.label}
              </Badge>
            </div>
            <div className="d-flex gap-2 flex-grow-1 justify-content-end">
              <Tooltip content="Clone">
                <button
                  className="btn border-start border-end f-10"
                  onClick={handleClone}
                >
                  <Layers2 size={16} strokeWidth={2} className="me-1" />
                </button>
              </Tooltip>
              <Tooltip content="Trim">
                <button className="btn border-end f-10" onClick={handleTrim}>
                  <Scissors size={16} strokeWidth={2} className="me-1" />
                </button>
              </Tooltip>
              <Tooltip content="Download">
                <button className="btn f-10" onClick={handleDownload}>
                  <Download size={16} strokeWidth={2} className="me-1" />
                </button>
              </Tooltip>
            </div>
          </div>
        </div>
      )}

      {viewMode === "list" && (
        <div className="card shadow-sm position-relative H">
          <span className="timeCar end-0 badge bg-primary position-absolute f-11">
            {item?.agencies && item?.agencies.length > 0
              ? getAgencyCategoryLabel(item?.agencies?.[0]?.category || 0)
              : ""}
          </span>
          <div
            className="card-content d-flex flex-row p-2"
            onClick={handleCardClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <div
              className="position-relative"
              style={{
                marginTop: "-0.5rem",
                marginLeft: "-0.5rem",
                marginBottom: "-0.6rem",
              }}
            >
              <div
                className="rounded ratio ratio-16x9 border  bg-secondary pointer"
                style={{ width: "165px", minWidth: "140px", cursor: "pointer" }}
              >
                <ProgressiveImage
                  src={thumbnailUrl}
                  className="position-absolute top-50 start-50 translate-middle"
                  alt="Media Thumbnail"
                  style={{
                    height: "100%",
                    width: "100%",
                    objectFit: isPortrait ? "contain" : "cover",
                    opacity: 1,
                  }}
                  useProgressiveLoading={useProgressiveLoading}
                />
                {isHovered && (
                  <div
                    className="media-hover-overlay position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-50"
                    style={{ transition: "opacity 0.2s ease-in-out" }}
                  >
                    <i
                      className="bi bi-play-fill text-white"
                      style={{ fontSize: "2rem" }}
                    ></i>
                  </div>
                )}
                <span className="timeCard position-absolute end-0">
                  {secondsToHms(item?.meta?.duration)}
                </span>
                <span
                  className="timeCard position-absolute badge bg-success bottom-0"
                  style={{ fontSize: "9px" }}
                >
                  {fileProcessingStatusToTextAndColor(item?.state).statusText}
                </span>
              </div>
            </div>
            <div
              className="card-body d-flex flex-column py-0"
              style={{ width: "calc(100% - 165px)" }}
            >
              <div className="text-truncate-2 text-capitalize fw-medium">
                {item?.title}
              </div>
              <p className="mb-3 mt-2 text-secondary">
                {item?.agencydata && `${formatEntityName(item?.agencydata)} | `}{" "}
                {formatTimestampToAgo(item?.ct)}
              </p>
              <div className="d-flex align-items-center gap-2">
                <SocialIcons socials={item?.socials} variant="list" />
                {uniqueTeamsDisplay.length > 0 &&
                  uniqueTeamsDisplay.map((teamName, index) => (
                    <span
                      key={`team-${index}`}
                      className="social-chip me-1 mb-1"
                    >
                      <i
                        className="bi bi-people-fill text-primary me-1"
                        style={{ fontSize: "0.8rem" }}
                      ></i>
                      <span
                        className="text-truncate"
                        style={{ maxWidth: "120px" }}
                      >
                        {teamName}
                      </span>
                    </span>
                  ))}
              </div>
            </div>
          </div>
          <div className="border-top d-flex">
            <div className="d-flex gap-2" style={{ marginLeft: "auto" }}>
              <Tooltip content="Clone">
                <button className="btn border-end" onClick={handleClone}>
                  <Layers2 size={16} strokeWidth={1} className="me-1" />
                </button>
              </Tooltip>
              <Tooltip content="Trim">
                <button
                  className="btn border-end"
                  onClick={handleTrim}
                  disabled={duration <= 0}
                  style={{ opacity: duration > 0 ? 1 : 0.5 }}
                >
                  <Scissors size={16} className="me-1" />
                </button>
              </Tooltip>
              <Tooltip content="Download">
                <button className="btn" onClick={handleDownload}>
                  <Download size={16} strokeWidth={1} className="me-1" />
                </button>
              </Tooltip>
            </div>
          </div>
        </div>
      )}

      {activeModal === "mediaDetails" && (
        <Modal
          isOpen={true}
          title={getWizardModalTitle()}
          onClose={() => {
            closeModal();
          }}
          width="1060px"
          closeOnOutsideClick={false}
          height="100vh"
          footer={
            <Button onClick={() => handleEvent("nextClick")}>
              Save & Next
            </Button>
          }
          updateModal={updateModal}
        >
          <Suspense
            fallback={
              <div className="d-flex justify-content-center p-4">
                Loading...
              </div>
            }
          >
            <MediaDetails
              isWizard={true}
              closeHandler={closeModal}
              closeModal={closeModal}
              openPublish={() => openModal("socialPublish")}
              clearFilters={false}
            />
          </Suspense>
        </Modal>
      )}

      {activeModal === "socialPublish" && (
        <Modal
          isOpen={true}
          title="Social Publish"
          onClose={closeModal}
          width="1060px"
          closeOnOutsideClick={false}
          height="100vh"
          footer={
            <div className="d-flex justify-content-between w-100">
              <Button
                type="outline-primary"
                onClick={() => handleEvent("backClick")}
              >
                Back
              </Button>
              <Button
                onClick={() => {
                  updateIsClearFilter(true);
                  updateIsFilter(false);
                  handleEvent("doneClick");
                }}
              >
                Done
              </Button>
            </div>
          }
          updateModal={updateModal}
        >
          <Suspense
            fallback={
              <div className="d-flex justify-content-center p-4">
                Loading...
              </div>
            }
          >
            <MediaPublish isWizard={true} closeHandler={closeModal} />
          </Suspense>
        </Modal>
      )}
      {isTrimmerOpen && (
        <Suspense
          fallback={
            <div className="d-flex justify-content-center p-4">Loading...</div>
          }
        >
          <VideoTrimmer
            isOpen={isTrimmerOpen}
            onClose={() => setIsTrimmerOpen(false)}
            media={item}
            onTrimComplete={(trimmedMediaResponse: any) => {
              setIsTrimmerOpen(false);
              setTrimmedMedia(trimmedMediaResponse);
              openModal("mediaDetails");
            }}
            trimMedia={trimMedia}
            updateModal={updateModal}
            presetList={presetList}
            preMediaList={preMediaList}
            postMediaList={postMediaList}
            getListMedia={getListMedia}
            getSearchedListMedia={getSearchedListMedia}
            getPresetList={getPresetList}
            getMedia={getMedia}
            currentTab={item?.previewfile ? DRIVE_TAB : undefined}
          />
        </Suspense>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  preMediaList: state.media.preMediaList.list,
  postMediaList: state.media.postMediaList.list,
  mediaList: state.media.mediaList.list,
  presetMediaList: state.media.presetMediaList.list,
  currentMediaDetailsForEdit: state.media.mediaDetails.media,
});

const mapDispatchToProps = {
  setMediaDetails,
  emptyAssetDetails,
  emptyMediaDetails,
  updateModal,
  trimMedia,
  getSearchedListMedia,
  getPresetList,
  getMedia,
  getListMedia,
  updateIsFilter,
  updateIsClearFilter,
  callRegisterCustomMediaOnCms,
  getTokenForDownload,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(DiscoverCard);
